import React from 'react';
import { Flex, Tag, Typography } from 'antd';
import { Button, Tooltip } from '@bettercomp/ui-components';
import { CheckOutlined } from '@ant-design/icons';
import type { NotificationOut } from 'service/notificationsService';
import { formatDate, YEAR_DATE_FORMAT } from 'utils/date';
import cn from './NotificationShell.module.scss';

type Props = {
  notification: NotificationOut;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  content: React.ReactNode;
};

export function NotificationShell({ notification, onMarkRead, onMarkUnread, content }: Props) {
  const isUnread = notification.read_at === null;
  // Resolved/expired rows are history: the backend rejects read-state changes
  // with 409, so the toggle is not rendered at all.
  const isLocked = notification.resolved || notification.expired;
  const handleMarkRead = () => onMarkRead(notification.id);
  const handleMarkUnread = () => onMarkUnread(notification.id);

  return (
    <Flex
      align="flex-start"
      justify="space-between"
      gap={8}
      className={cn.shell}
      data-testid="notification-shell"
    >
      <Flex vertical gap={2} flex={1}>
        {content}
        <Typography.Text type="secondary">{formatDate(notification.created_at, YEAR_DATE_FORMAT)}</Typography.Text>
      </Flex>
      {isLocked && <Tag data-testid="notification-status-tag">{notification.resolved ? 'Resolved' : 'Expired'}</Tag>}
      {!isLocked &&
        (isUnread ? (
          <Tooltip title="Mark as read">
            <Button
              shape="circle"
              variant="outlined"
              color="primary"
              icon={<CheckOutlined />}
              size="small"
              onClick={handleMarkRead}
              data-testid="unread-indicator"
            />
          </Tooltip>
        ) : (
          <Tooltip title="Mark as unread">
            <Button
              shape="circle"
              variant="filled"
              color="default"
              icon={<CheckOutlined />}
              size="small"
              onClick={handleMarkUnread}
              data-testid="read-indicator"
            />
          </Tooltip>
        ))}
    </Flex>
  );
}
